import type { ComponentPublicInstance } from 'vue';

/**
 * Resolve the real `<input>` a design-system `AkInput` renders inside its own template.
 *
 * `AkInput` is a component, so a `ref` on it yields the instance rather than an element, and
 * APIs that demand an `HTMLInputElement` — Google Places, `v-mask`, `setSelectionRange` — cannot
 * take it directly. Prefer this over `document.getElementById`: the same field is rendered more
 * than once on several pages (three times on the brand addresses page, twice in the cart's
 * billing wrapper, where one copy is only `v-show`-hidden), so a document-wide lookup on a
 * shared `id` binds every caller to the first match.
 *
 * Returns `null` rather than throwing for every shape the ref can legitimately take: not yet
 * mounted, already unmounted (Vue nulls the ref, and async SDK callbacks routinely land after
 * teardown), or rendered as a comment placeholder by a falsy `v-if`.
 */
export function resolveAkInputElement(component: unknown): HTMLInputElement | null {
  const root = (component as ComponentPublicInstance | undefined)?.$el;

  // `$el` is a comment node for a `v-if`-ed root and a text node for a fragment root; only an
  // element can be queried.
  return root instanceof HTMLElement ? root.querySelector('input') : null;
}
